<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
    <div class="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl relative overflow-hidden animate-fade-in my-8">
      <div class="flex justify-between items-center mb-5 pb-3 border-b border-gray-100">
        <div>
          <h2 class="text-xl font-bold text-gray-900">
            {{ shift ? 'Editar Turno' : 'Registrar Turno Individual' }}
          </h2>
          <p class="text-xs text-gray-500">Asigna fecha, tarifa y labor al personal seleccionado.</p>
        </div>
        <button 
          type="button"
          class="text-gray-400 hover:text-gray-600 text-lg p-1 cursor-pointer" 
          @click="$emit('close')"
        >
          <i class="fa fa-times" />
        </button>
      </div>

      <form class="space-y-4" @submit.prevent="handleSubmit">
        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">Empleado *</label>
          <select
            v-model="form.employee_id"
            required
            class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
            @change="onEmployeeSelect"
          >
            <option value="" disabled>Selecciona un empleado...</option>
            <option v-for="emp in allActiveEmployees" :key="emp.id" :value="emp.id">
              {{ emp.name }} ({{ emp.role_or_job || 'Personal' }}) - Tarifa: ${{ emp.rate }}
            </option>
          </select>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Fecha del Turno *</label>
            <input
              v-model="form.date"
              type="date"
              required
              class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Tipo de Turno / Labor *</label>
            <select
              v-model="form.shift_type"
              required
              class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
              @change="onShiftTypeChange"
            >
              <option value="Jornada Completa">Jornada Completa</option>
              <option value="Turno Diurno">Turno Diurno</option>
              <option value="Turno Nocturno">Turno Nocturno</option>
              <option value="Medio Turno">Medio Turno</option>
              <option value="Destajo / Producción">Destajo / Producción</option>
              <option value="Horas Extras">Horas Extras</option>
              <option value="Feriado / Fin de Semana">Feriado / Fin de Semana</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Evento / Proyecto / Carpeta</label>
            <input
              v-model="form.event_name"
              type="text"
              placeholder="Ej. Concierto Caracas, Seguridad Centro..."
              class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">Horas Trabajadas (Opcional)</label>
            <input
              v-model.number="form.hours"
              type="number"
              step="0.5"
              min="0"
              placeholder="Ej. 3.0"
              class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold"
              @input="onHoursChange"
            />
          </div>

          <div class="sm:col-span-2">
            <label class="block text-xs font-bold text-gray-700 mb-1">Monto a Pagar ($ USD) *</label>
            <input
              v-model.number="form.amount"
              type="number"
              step="0.01"
              min="0"
              required
              placeholder="0.00"
              class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold text-gray-900 focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            />
            <p class="text-[10px] text-gray-500 mt-1 font-medium">Aprox. {{ formatBs(form.amount || 0) }}</p>
          </div>

          <!-- Smart Rate Calculation Pill -->
          <div v-if="selectedShiftEmployee" class="col-span-1 sm:col-span-2 bg-orange-50 border border-orange-200/80 p-2.5 rounded-xl text-xs flex flex-wrap items-center justify-between gap-2 text-orange-950 font-medium">
            <div class="flex items-center gap-1.5">
              <i class="fa fa-info-circle text-orange-600" />
              <span>Tarifa: <strong class="text-orange-900">${{ selectedShiftEmployee.rate }}</strong> ({{ translateSalaryType(selectedShiftEmployee.type_salary) }})</span>
            </div>
            <div v-if="form.hours > 0" class="font-bold text-orange-800 bg-orange-100/90 border border-orange-300 px-2.5 py-0.5 rounded-lg text-xs">
              {{ form.hours }} hrs × ${{ selectedShiftEmployee.rate }} = ${{ (form.hours * selectedShiftEmployee.rate).toFixed(2) }}
            </div>
          </div>
        </div>

        <div>
          <label class="block text-xs font-bold text-gray-700 mb-1">Observaciones / Tarea realizada</label>
          <input
            v-model="form.notes"
            type="text"
            placeholder="Ej. Turno de cocina / Descarga de camión / 50 piezas terminadas"
            class="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            class="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 cursor-pointer"
            @click="$emit('close')"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="submitting"
            class="px-6 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm shadow-md transition-all disabled:opacity-50 cursor-pointer active:scale-95"
          >
            {{ submitting ? 'Guardando...' : (shift ? 'Actualizar Turno' : 'Registrar Turno') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SingleShiftModal',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: null,
    },
    targetEmployee: {
      type: Object,
      default: null,
    },
    allActiveEmployees: {
      type: Array,
      default: () => [],
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: ['close', 'submit'],

  data() {
    return {
      form: this.getInitialFormData(),
    }
  },

  computed: {
    selectedShiftEmployee() {
      return this.allActiveEmployees.find(e => e.id === this.form.employee_id) || null
    },
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.form = this.getInitialFormData()
      }
    },
    shift() {
      this.form = this.getInitialFormData()
    },
    targetEmployee() {
      this.form = this.getInitialFormData()
    },
  },

  methods: {
    getInitialFormData() {
      if (this.shift) {
        return {
          employee_id: this.shift.employee_id,
          date: this.shift.date ? this.shift.date.split('T')[0] : new Date().toISOString().split('T')[0],
          shift_type: this.shift.shift_type || 'Jornada Completa',
          event_name: this.shift.event_name || '',
          hours: this.shift.hours,
          amount: this.shift.amount,
          notes: this.shift.notes || '',
        }
      }

      const targetEmpId = this.targetEmployee ? this.targetEmployee.id : (this.allActiveEmployees?.[0]?.id || '')
      const targetEmp = this.allActiveEmployees.find(e => e.id === targetEmpId)
      const defaultHours = targetEmp && targetEmp.type_salary === 'hourly' ? 1 : null
      const defaultAmount = targetEmp ? (defaultHours ? targetEmp.rate * defaultHours : targetEmp.rate) : 0

      return {
        employee_id: targetEmpId,
        date: new Date().toISOString().split('T')[0],
        shift_type: 'Jornada Completa',
        event_name: targetEmp?.event_name || '',
        hours: defaultHours,
        amount: defaultAmount,
        notes: '',
      }
    },

    onHoursChange() {
      const emp = this.selectedShiftEmployee
      if (!emp) return
      const hours = Number(this.form.hours) || 0
      const rate = Number(emp.rate) || 0
      if (emp.type_salary === 'hourly' || this.form.shift_type === 'Horas Extras' || emp.type_salary === 'piecework') {
        if (hours > 0) {
          this.form.amount = parseFloat((hours * rate).toFixed(2))
        } else {
          this.form.amount = rate
        }
      }
    },

    onShiftTypeChange() {
      const emp = this.selectedShiftEmployee
      if (!emp) return
      const hours = Number(this.form.hours) || 0
      const rate = Number(emp.rate) || 0
      if (this.form.shift_type === 'Horas Extras' && hours > 0) {
        this.form.amount = parseFloat((hours * rate).toFixed(2))
      }
    },

    onEmployeeSelect() {
      const emp = this.selectedShiftEmployee
      if (emp && !this.shift) {
        const hours = Number(this.form.hours) || 0
        if ((emp.type_salary === 'hourly' || emp.type_salary === 'piecework') && hours > 0) {
          this.form.amount = parseFloat((hours * Number(emp.rate)).toFixed(2))
        } else {
          this.form.amount = Number(emp.rate) || 0
        }
      }
    },

    handleSubmit() {
      this.$emit('submit', { ...this.form })
    },

    translateSalaryType(type) {
      const map = {
        shift: 'Por Turno',
        piecework: 'A Destajo',
        hourly: 'Por Hora',
        fixed: 'Sueldo Fijo',
      }
      return map[type] || type || 'Turno'
    },

    formatBs(val) {
      const num = (parseFloat(val) || 0) * (this.currentExchangeRate || 1)
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Bs.'
    },
  },
}
</script>
